/**
 * @copyright Copyright 2016-2018 The MathWorks, Inc.
 */
define([
    "dojo/dom-geometry",
    "dojo/on",
    "dojo/window",
    "./stopEvent"
], function (domGeometry, on, win, stopEvent) {

    let getDialogPositionAfterMove = (dialogDimension, windowDimension, event,
                                      dragOffset, dialogMoveLocked, bindingNodeDimension) => {
        let dialogPosition = {};

        if (bindingNodeDimension) {
            if (!dialogMoveLocked.yLocked) {
                dialogPosition.t = Math.max(0, Math.max(bindingNodeDimension.y,
                    Math.min(windowDimension.h - dialogDimension.h,
                        Math.min(event.pageY - dragOffset.y,
                            bindingNodeDimension.y + bindingNodeDimension.h -
                            dialogDimension.h))));
            } else {
                dialogPosition.t = dialogDimension.y;
            }

            if (!dialogMoveLocked.xLocked) {
                dialogPosition.l = Math.max(0, Math.max(bindingNodeDimension.x,
                    Math.min(windowDimension.w - dialogDimension.w,
                        Math.min(event.pageX - dragOffset.x,
                            bindingNodeDimension.x + bindingNodeDimension.w -
                            dialogDimension.w))));
            } else {
                dialogPosition.l = dialogDimension.x;
            }
        } else {
            dialogPosition.l = Math.max(0,
                Math.min(event.pageX - dragOffset.x,
                    windowDimension.w - dialogDimension.w));
            dialogPosition.t = Math.max(0,
                Math.min(event.pageY - dragOffset.y,
                    windowDimension.h - dialogDimension.h));
        }

        return dialogPosition;
    };

    /**
     * Draggable provides drag and drop API for dialogs
     * @module mw-dialogs/controller/Draggable
     */
    return {

        dragEvent: "move",

        /**
         * Make a dialog draggable by dragging the header
         *
         * @static
         *
         * @param {Object} [args] - argument object
         * @param {Object} [args.dialog] - The dialog instance
         * @param {Object} [args.bindingNode] - DOM Node which constraints the of dialog
         */
        draggable (args) {
            let that = this;

            // Dialog instance
            let {dialog, bindingNode} = args;

            // Specifies on what element the drag interaction starts.
            let handle = dialog._titleBar;

            let isDragReady = false,
                dragOffset = {
                    x: 0,
                    y: 0
                },
                dialogPosition = {
                    l: 0,
                    t: 0
                },
                dialogMoveLocked = {
                    xLocked: false,
                    yLocked: false
                },
                dialogDimension,
                windowDimension;

            // Dimension of bindingNode which specifies the boundary to constrain the movement
            // of the dialog.
            let bindingNodeDimension;

            let addDraggableHandler = () => {

                dialog._trackEventListener(on(handle, 'mousedown', (e) => {

                    dialog.bringToTop();

                    // Only drag on left click
                    if (e.button !== 0) {
                        return;
                    }

                    if (e.target === dialog._closeSVG || e.target === dialog._closeNode) {
                        return;
                    }

                    stopEvent(e);

                    isDragReady = true;
                    windowDimension = win.getBox();
                    dialogDimension = domGeometry.position(dialog.domNode);

                    if (typeof bindingNode === 'object') {
                        bindingNodeDimension = domGeometry.position(bindingNode);
                        dialogMoveLocked.xLocked = dialogDimension.w >= bindingNodeDimension.x
                            + bindingNodeDimension.w
                            || dialogDimension.w >= windowDimension.w - bindingNodeDimension.x
                            || dialogDimension.w >= bindingNodeDimension.w;

                        dialogMoveLocked.yLocked = dialogDimension.h >= bindingNodeDimension.y
                            + bindingNodeDimension.h
                            || dialogDimension.h >= windowDimension.h - bindingNodeDimension.y
                            || dialogDimension.h >= bindingNodeDimension.h;
                    }

                    dragOffset.x = e.pageX - dialogDimension.x;
                    dragOffset.y = e.pageY - dialogDimension.y;
                }));

                dialog._trackEventListener(on(document, 'mouseup', () => {
                    isDragReady = false;
                }));

                dialog._trackEventListener(on(document, 'mousemove', (e) => {

                    if (isDragReady) {

                        stopEvent(e);

                        dialogDimension = domGeometry.position(dialog.domNode);

                        if (typeof bindingNode === 'object') {
                            dialogPosition = getDialogPositionAfterMove(dialogDimension,
                                windowDimension, e, dragOffset, dialogMoveLocked,
                                bindingNodeDimension);

                            dialog._specifiedPosition = {
                                x: dialogPosition.l - bindingNodeDimension.x,
                                y: dialogPosition.t - bindingNodeDimension.y
                            };
                        } else {
                            dialogPosition = getDialogPositionAfterMove(dialogDimension,
                                windowDimension, e, dragOffset);

                            dialog._specifiedPosition = {
                                x: dialogPosition.l,
                                y: dialogPosition.t
                            };
                        }

                        dialog.domNode.style.left = `${dialogPosition.l}px`;
                        dialog.domNode.style.top = `${dialogPosition.t}px`;
                        dialog._isCenter = false;

                        on.emit(dialog.domNode, that.dragEvent, {
                            bubbles: false,
                            position: {
                                x: dialogPosition.l,
                                y: dialogPosition.t
                            }
                        });
                    }
                }));
            };

            addDraggableHandler();
        }
    };
});